import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

type EditBookingVehiclePriceModalProps = {
  open: boolean;
  vehicleName: string;
  currentPrice: number;
  onClose: () => void;
  onSave: (price: number) => void;
};

export const EditBookingVehiclePriceModal = ({
  open,
  vehicleName,
  currentPrice,
  onClose,
  onSave,
}: EditBookingVehiclePriceModalProps) => {
  const [price, setPrice] = useState('');

  useEffect(() => {
    if (open) {
      setPrice(String(Math.max(0, Math.round(currentPrice))));
    }
  }, [open, currentPrice]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const parsedPrice = Number(price);

  const isValid =
    price.trim() !== '' &&
    Number.isInteger(parsedPrice) &&
    parsedPrice >= 0;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isValid) return;

    const roundedPrice =
      Math.round((parsedPrice + Number.EPSILON) * 100) / 100;

    onSave(parsedPrice);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/45 px-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-booking-price-title"
        className="w-full max-w-[440px] rounded-[20px] bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2
              id="edit-booking-price-title"
              className="font-host text-[20px] font-bold text-[#1A1A1A]"
            >
              Edit Booking Price
            </h2>

            <p className="mt-1 font-host text-[13px] text-[#757575]">
              {vehicleName}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close price editor"
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#757575] transition-colors hover:bg-[#F2F2F2] hover:text-black"
          >
            <X size={19} />
          </button>
        </div>

        <div className="mt-6">
          <label
            htmlFor="booking-vehicle-price"
            className="mb-2 block font-host text-[13px] font-semibold text-[#1A1A1A]"
          >
            Price for this booking
          </label>

          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-host text-[16px] font-semibold text-[#1A1A1A]">
              €
            </span>

          <input
            id="booking-vehicle-price"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="0"
            value={price}
            onChange={(event) => {
                const nextValue = event.target.value;

                // Empty value is allowed while editing.
                // Otherwise, accept digits only.
                if (nextValue === '' || /^\d+$/.test(nextValue)) {
                setPrice(nextValue);
                }
            }}
            autoFocus
            className="h-[52px] w-full rounded-[10px] border border-[#D9D9D9] bg-white pl-9 pr-4 font-host text-[16px] outline-none transition-colors focus:border-[#95BE42] focus:ring-2 focus:ring-[#95BE42]/20"
            />
          </div>

          {!isValid && price !== '' && (
            <p className="mt-2 font-host text-[12px] text-red-500">
              Enter a valid price greater than or equal to zero.
            </p>
          )}
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-[44px] rounded-[22px] border border-[#D9D9D9] px-6 font-host text-[14px] font-semibold text-[#1A1A1A] transition-colors hover:bg-[#F5F5F5]"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={!isValid}
            className="h-[44px] rounded-[22px] bg-[#2D561C] px-6 font-host text-[14px] font-semibold text-white transition-colors hover:bg-[#244616] disabled:cursor-not-allowed disabled:bg-[#D9D9D9] disabled:text-[#999999]"
          >
            Save Price
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};